import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";

// Ordre d'affichage des catégories sous chaque date (défini par le propriétaire)
const CATEGORY_ORDER = [
  "PACK PREMIUM VIP",
  "VIP 2 PARTERRE",
  "VIP 3 PARTERRE",
  "VIP 4 TRIBUNE BASSE",
  "PLATINIUM",
  "PLATINUM 1",
  "PLATINUM 4",
  "PLATINUM 5",
  "PLATINUM 6",
  "PLATINUM 7",
  "PLATINIUM 8",
  "PLATINUM 8",
  "PLATINUM 10",
  "PLATINUM 12",
  "DIAMANT PARTERRE",
  "PARTERRE ASSIS PRESTIGE PARTERRE",
  "PARTERRE ASSIS PRESTIGE",
  "TRIBUNE OR",
  "CARRE OR",
  "CARRE OR ALLEE",
  "CAT OR",
  "CAT OR CS VR",
  "CAT OR VR",
  "CAT 1 PARTERRE",
  "CAT 1 LOWER",
  "CATEGORIE 1 ALLEE",
  "CATEGORIE 1",
  "CAT 1 UPPER",
  "CAT 1 CS VR",
  "CAT 1 VR",
  "CAT 2 PARTERRE",
  "CATEGORIE 2",
  "CAT 2 UPPER",
  "CAT 2 CS VR",
  "CAT 2 VR",
  "CAT 3",
  "CAT 3 CS VR",
  "CAT 3 VR",
  "CAT 4",
  "UPPER",
];
const CAT_RANK = new Map(CATEGORY_ORDER.map((c, i) => [c, i]));
const catRank = (c) => {
  const r = CAT_RANK.get(String(c || "").trim().toUpperCase());
  return r === undefined ? 999 : r;
};

function groupByDate(tickets) {
  const map = new Map();
  for (const t of tickets) {
    const k = t.date;
    if (!map.has(k)) map.set(k, { date: k, order_index: t.order_index, items: [] });
    map.get(k).items.push(t);
  }
  return Array.from(map.values()).sort(
    (a, b) => a.order_index - b.order_index
  );
}

// Group consecutive seats (same categorie, block, rang and contiguous siege numbers) into lots
function buildLots(items) {
  const seatNum = (s) => {
    const n = parseInt(String(s).replace(/[^\d-]/g, ""), 10);
    return Number.isNaN(n) ? null : n;
  };
  // Sort by categorie (custom order), block, rang, siege number
  const sorted = [...items].sort((a, b) => {
    const ra = catRank(a.categorie);
    const rb = catRank(b.categorie);
    if (ra !== rb) return ra - rb;
    const ka = `${a.categorie}|${a.block}|${a.rang}`;
    const kb = `${b.categorie}|${b.block}|${b.rang}`;
    if (ka !== kb) return ka.localeCompare(kb);
    const sa = seatNum(a.siege);
    const sb = seatNum(b.siege);
    if (sa == null && sb == null) return 0;
    if (sa == null) return 1;
    if (sb == null) return -1;
    return sa - sb;
  });
  const lots = [];
  for (const t of sorted) {
    const last = lots[lots.length - 1];
    const sn = seatNum(t.siege);
    const sameKey =
      last &&
      last.categorie === t.categorie &&
      last.block === t.block &&
      last.rang === t.rang;
    if (sameKey && sn != null && last.lastSeat != null && sn === last.lastSeat + 1) {
      last.seats.push(t.siege);
      last.prices.push(t.prix);
      last.lastSeat = sn;
      last.count += 1;
    } else {
      lots.push({
        categorie: t.categorie,
        block: t.block,
        rang: t.rang,
        seats: [t.siege],
        prices: [t.prix],
        lastSeat: sn,
        count: 1,
      });
    }
  }
  return lots;
}

function formatSeats(lot) {
  if (lot.count === 1) return lot.seats[0] || "—";
  const first = lot.seats[0];
  const last = lot.seats[lot.seats.length - 1];
  return `${first} → ${last}`;
}

function formatPrice(n) {
  if (n == null) return null;
  return Number.isInteger(n) ? `${n}` : n.toFixed(2).replace(/\.?0+$/, "");
}

function lotPriceDisplay(lot) {
  const prices = lot.prices.filter((p) => p != null && !Number.isNaN(p));
  if (prices.length === 0) return { unit: null, total: null };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const allSet = prices.length === lot.count;
  const total = prices.reduce((s, p) => s + p, 0);
  if (min === max) {
    return {
      unit: formatPrice(min),
      total: allSet ? formatPrice(total) : null,
      uniform: true,
    };
  }
  return {
    unit: `${formatPrice(min)}–${formatPrice(max)}`,
    total: allSet ? formatPrice(total) : null,
    uniform: false,
  };
}

export default function Vente() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateFilter, setDateFilter] = useState("__all__");
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [{ data }, { data: settings }] = await Promise.all([
          api.get("/public/tickets"),
          api.get("/public/settings"),
        ]);
        if (alive) {
          setTickets(data.tickets || []);
          setShowPrices(settings.show_prices_public !== false);
        }
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail) || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 30000); // auto-refresh every 30s
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const allDates = useMemo(() => {
    const seen = new Map();
    for (const t of tickets) {
      if (!seen.has(t.date)) seen.set(t.date, t.order_index);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([d]) => d);
  }, [tickets]);

  const visibleTickets = useMemo(() => {
    let arr = tickets;
    if (dateFilter !== "__all__") arr = arr.filter((t) => t.date === dateFilter);
    return arr;
  }, [tickets, dateFilter]);

  const groups = useMemo(() => groupByDate(visibleTickets), [visibleTickets]);

  const hasFilters = dateFilter !== "__all__";
  const clearFilters = () => {
    setDateFilter("__all__");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="vente-page">
      {/* Top banner */}
      <header
        className="relative border-b border-neutral-200"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,10,10,0.85), rgba(10,10,10,0.85)), url('https://static.prod-images.emergentagent.com/jobs/e660046c-af3a-43cb-ae12-50c06fd2d107/images/a7c00373286faffab6d207c81655b3e8559e3bf87ba3a59fb15a8f69269cf2ff.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 py-14 lg:py-20 text-white fade-in-up grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.4em] text-white/60">
              Billetterie · Tournée
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-3">
              Céline Dion
            </h1>
            <p className="mt-4 text-sm text-white/70 max-w-md">
              Stock disponible en temps réel. Lecture seule. Mise à jour automatique.
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <img
              src="https://customer-assets.emergentagent.com/job_celine-ticket-admin/artifacts/rl9e2u2n_image.png"
              alt="Céline Dion · Paris 2026 · Sept–Oct"
              className="w-full max-w-[200px] shadow-2xl ring-1 ring-white/10"
              data-testid="vente-hero-poster"
            />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Filters */}
        {tickets.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <span className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-400 mr-1">
              Filtrer
            </span>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger
                className="w-[280px] h-12 rounded-full border-2 border-black bg-white text-black hover:bg-neutral-50 transition-colors focus:ring-0 focus:outline-none px-5 font-medium"
                data-testid="vente-date-filter"
              >
                <SelectValue placeholder="Toutes les dates" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-2 border-black">
                <SelectItem value="__all__">Toutes les dates</SelectItem>
                {allDates.map((d) => (
                  <SelectItem key={d} value={d} className="capitalize">
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs uppercase tracking-widest text-neutral-500 hover:text-black flex items-center gap-1 px-3 h-12"
                data-testid="vente-clear-filters"
              >
                <X className="w-3.5 h-3.5" /> Effacer
              </button>
            )}
          </div>
        )}

        {loading && (
          <div
            className="font-mono-data text-xs text-neutral-400"
            data-testid="vente-loading"
          >
            Chargement du stock…
          </div>
        )}
        {error && (
          <div
            className="border border-red-500 text-red-600 text-sm px-3 py-2"
            data-testid="vente-error"
          >
            {error}
          </div>
        )}
        {!loading && !error && groups.length === 0 && (
          <div
            className="border border-neutral-200 p-12 text-center"
            data-testid="vente-empty"
          >
            <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-neutral-400">
              {hasFilters ? "Aucun résultat" : "Stock vide"}
            </div>
            <p className="mt-3 font-serif-display text-3xl">
              {hasFilters
                ? "Aucun billet ne correspond à ces filtres."
                : "Aucun billet disponible pour le moment."}
            </p>
            {!hasFilters && (
              <p className="mt-2 text-sm text-neutral-500">
                Revenez bientôt — le stock est mis à jour en continu.
              </p>
            )}
          </div>
        )}

        <div className="space-y-12" data-testid="vente-list">
          {groups.map((g) => {
            const sortedItems = [...g.items].sort((a, b) => {
              const ra = catRank(a.categorie);
              const rb = catRank(b.categorie);
              if (ra !== rb) return ra - rb;
              const k = `${a.categorie}|${a.block}|${a.rang}`.localeCompare(
                `${b.categorie}|${b.block}|${b.rang}`
              );
              if (k !== 0) return k;
              const sa = parseInt(String(a.siege).replace(/[^\d-]/g, ""), 10);
              const sb = parseInt(String(b.siege).replace(/[^\d-]/g, ""), 10);
              if (Number.isNaN(sa) && Number.isNaN(sb)) return 0;
              if (Number.isNaN(sa)) return 1;
              if (Number.isNaN(sb)) return -1;
              return sa - sb;
            });
            // Mark each row as "end of lot" when the next row is not contiguous
            const lotEndIdx = new Set();
            for (let i = 0; i < sortedItems.length; i++) {
              const cur = sortedItems[i];
              const next = sortedItems[i + 1];
              if (!next) {
                lotEndIdx.add(i);
                continue;
              }
              const sameKey =
                cur.categorie === next.categorie &&
                cur.block === next.block &&
                cur.rang === next.rang;
              const cn = parseInt(String(cur.siege).replace(/[^\d-]/g, ""), 10);
              const nn = parseInt(String(next.siege).replace(/[^\d-]/g, ""), 10);
              const consecutive =
                !Number.isNaN(cn) && !Number.isNaN(nn) && nn === cn + 1;
              if (!sameKey || !consecutive) {
                lotEndIdx.add(i);
              }
            }
            return (
            <section key={g.date} data-testid={`vente-group-${g.date.replace(/\s+/g, "-")}`}>
              <div className="border-b border-black pb-4 mb-6">
                <h2
                  className="capitalize text-5xl sm:text-6xl lg:text-7xl text-black font-medium leading-none"
                  style={{ fontFamily: "'Playfair Display', serif", fontVariantNumeric: "lining-nums tabular-nums" }}
                >
                  {g.date}
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs sm:text-sm uppercase tracking-[0.2em] text-neutral-700 font-semibold border-b-2 border-neutral-300">
                      <th className="py-4 pr-4 font-semibold">Catégorie</th>
                      <th className="py-4 pr-4 font-semibold">Block</th>
                      <th className="py-4 pr-4 font-semibold">Rang</th>
                      <th className="py-4 pr-4 font-semibold">Siège</th>
                      {showPrices && (
                        <th className="py-4 font-semibold text-right">Prix (€)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((t, idx) => (
                      <tr
                        key={t.id}
                        className={`hover:bg-neutral-50 transition-colors ${
                          lotEndIdx.has(idx)
                            ? "border-b-[3px] border-neutral-400"
                            : "border-b border-neutral-100"
                        }`}
                        data-testid={`vente-row-${t.id}`}
                      >
                        <td className="py-3 pr-4">{t.categorie}</td>
                        <td className="py-3 pr-4 font-mono-data text-neutral-700">
                          {t.block || "—"}
                        </td>
                        <td className="py-3 pr-4 font-mono-data text-neutral-700">
                          {t.rang || "—"}
                        </td>
                        <td className="py-3 pr-4 font-mono-data text-neutral-700">
                          {t.siege || "—"}
                        </td>
                        {showPrices && (
                          <td className="py-3 font-mono-data text-right">
                            {t.prix == null ? (
                              <span className="text-neutral-300">—</span>
                            ) : (
                              <span className="text-black">
                                {Number.isInteger(t.prix) ? t.prix : t.prix.toFixed(2).replace(/\.?0+$/, "")} €
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
          })}
        </div>
      </main>

      <footer className="border-t border-neutral-200 mt-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6 text-center font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-400">
          Stock mis à jour en temps réel · Lecture seule
        </div>
      </footer>
    </div>
  );
}
